<template>
  <main id="content">
    <div class="l-main-content__inner">
      <div class="c-page-header">
        <h1 class="c-page-header-title">進級処理<span class="small">（先生がデータを作成）</span></h1>
        <div class="c-page-header__return">
          <a
            :href="`/teacher/class/${targetAcademicSessions?.from_academic_session.year}/student/next_grade`"
            class="c-button-white -return -teacher">
            <span class="label">戻る</span>
          </a>
        </div>
      </div>

      <template v-if="canNextGrade">
        <template v-if="fromAcademicYearStudentsRegistrationStatus">
          <p class="c-page-explanation -attention">
            新年度の学年・学級の登録は、名簿データを作成・アップロードして行います。<br>
            ※手順については<a href="https://manavi.wikipy.jp/teacher/p712" target="_blank">コチラ</a>をご参照ください。
          </p>

          <section v-if="isStudentsRegisteredInFromAcademicYear">

            <p class="p-grade-target-text display">
              <span class="target-year display">{{ targetAcademicSessions.from_academic_session.year }}年度 {{ fromAcademicYear() }}年生</span>を
              <span class="target-year display">{{ targetAcademicSessions.to_academic_session.year }}年度 {{ toAcademicYear }}年生</span>にする処理を行います
            </p>

            <div class="c-page-section">
              <ol class="c-step">
                <!-- STEP1: 学級作成 -->
                <li class="item on" id="step1">
                  <div class="item__inner">
                    <div class="item__title">
                      <figure>
                        <figcaption>STEP1</figcaption>
                        <img
                          src="../../../../djangoapp/assets/teacher/img/common/icon_make_class.png"
                          alt="add classroom"
                        >
                      </figure>
                    </div>
                    <div class="item__body">
                      <div class="title">
                        学級作成
                      </div>
                      <div class="body -top">
                        <div>
                          <p class="text">
                            進級後の生徒を所属させる新しい学級（{{ targetAcademicSessions.to_academic_session.year }}年度 {{ toAcademicYear }}年生の学級）を作成します。
                          </p>
                          <div class="p-grade-frame">
                            <div class="p-grade-frame-title-wrapper">
                              <p class="p-grade-frame-title">{{ targetAcademicSessions.to_academic_session.year }}年度 {{ toAcademicYear }}年生の学級</p>
                              <button class="c-button-white -prefix-icon -reload" @click="reloadClassrooms">
                                <span class="label">登録内容の確認</span>
                              </button>
                            </div>
                            <div class="p-grade-frame-contents">
                              <ul v-if="toClassrooms">
                                <li v-for="classroom in toClassrooms" :key="classroom.id">{{ classroom.name }}</li>
                              </ul>
                              <ul v-else>
                                <li>まだ登録されていません</li>
                              </ul>
                            </div>
                          </div>
                        </div>
                        <div class="btn -wide">
                          <a
                            :href="`/teacher/class/add/?academic_session_year=${targetAcademicSessions.to_academic_session.year}&academic_year=${toAcademicYear}`"
                            target="_blank"
                            class="c-button-blue -arrow -type01"
                          >
                            <span class="label">学級登録画面へ</span>
                          </a>
                        </div>
                      </div>
                    </div>
                  </div>
                </li>

                <!-- STEP2: 進級処理用の名簿ダウンロード -->
                <li class="item on" id="step2">
                  <div class="item__inner">
                    <div class="item__title">
                      <figure>
                        <figcaption>STEP2</figcaption>
                        <img
                          src="../../../../djangoapp/assets/teacher/img/common/icon_regi_step1.png"
                          alt="download students roster"
                        >
                      </figure>
                    </div>
                    <div class="item__body">
                      <div class="title">
                        進級処理用の名簿ダウンロード
                      </div>
                      <div class="body">
                        <p class="text">右のボタンを押し、生徒名簿テンプレートを保存してください。</p>
                        <div class="btn -wide">
                          <button
                            type="button"
                            name="button"
                            class="c-button-blue -small-regular"
                            @click="downloadStudentsRoster"
                          >
                            <span class="label -download">ダウンロード</span>
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>
                </li>

                <!-- STEP3: 進級処理用の名簿データを作成する -->
                <li class="item on">
                  <div class="item__inner">
                    <div class="item__title">
                      <figure>
                        <figcaption>STEP3</figcaption>
                        <img
                          src="../../../../djangoapp/assets/teacher/img/common/icon_regi_step2.png"
                          alt="edit students roster data"
                          class="adjust1"
                        >
                      </figure>
                    </div>
                    <div class="item__body">
                      <div class="title">
                        進級処理用の名簿データを作成する
                      </div>
                      <div class="body">
                        <p class="text">
                          STEP2でダウンロードしたファイルをパソコンで開き、進級後の名簿を作成してください。<br>
                          具体的な手順については<a href="https://manavi.wikipy.jp/teacher/p698" class="c-parts-link" target="_blank">コチラ</a>を参照ください。</p>
                      </div>
                    </div>
                  </div>
                </li>

                <!-- STEP4: 進級処理用の名簿データをアップロードする -->
                <li class="item on">
                  <div class="item__inner">
                    <div class="item__title">
                      <figure>
                        <figcaption>STEP4</figcaption>
                        <img
                          src="../../../../djangoapp/assets/teacher/img/common/icon_regi_step3.png"
                          alt="upload students roster"
                        >
                      </figure>
                    </div>
                    <div class="item__body">
                      <div class="title">
                        進級処理用の名簿データをアップロードする
                      </div>
                      <div class="body">
                        <p class="text">右のボタンを押し、STEP3で作成した進級処理用の名簿データをアップロードしてください。</p>
                        <div class="btn -wide">
                          <button
                            type="button"
                            name="upload-button"
                            class="c-button-blue -small-regular"
                            @click="showUploadModal"
                          >
                            <span class="label -upload">アップロード</span>
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>
                </li>

                <template v-if="uploadedResult">
                  <template v-if="uploadedResult.error">
                    <!-- STEP5: 登録結果（失敗） -->
                    <li class="item on item--error">
                      <div class="item__inner">
                        <div class="item__title">
                          <figure>
                            <figcaption>STEP5</figcaption>
                            <img
                              src="../../../../djangoapp/assets/teacher/img/common/icon_regi_step4_error.png"
                              alt="import failed"
                              class="adjust2"
                            >
                          </figure>
                        </div>
                        <div class="item__body">
                          <div class="title">
                            登録結果
                          </div>
                          <div class="result">
                            <p
                              class="errorText"
                              v-html="uploadedResult.error.message"
                            ></p>
                            <dl class="list">
                              <dt class="list__title">ファイル名</dt>
                              <dd class="list__data">
                                <p class="text">{{ uploadedResult.filename }}</p>
                              </dd>
                              <dt class="list__title">結果</dt>
                              <dd class="list__data">
                                <p class="text text--error">
                                  <span>失敗</span>（読み込めませんでした。）
                                </p>
                              </dd>
                              <dt class="list__title">エラー内容</dt>
                              <dd class="list__data">
                                <div class="c-table-list__wrapper">
                                  <div class="c-table-list">
                                    <dl class="c-table-list__inner">
                                      <div class="c-table-list__item -error">
                                        <dt>対象行</dt>
                                        <dt>エラーコード</dt>
                                        <dd>詳細</dd>
                                      </div>
                                      <div
                                        class="c-table-list__item -error"
                                        v-for="(detail, index) in uploadedResult.error.data.details"
                                        :key="index"
                                      >
                                        <dt>{{ detail.row }}</dt>
                                        <dt>{{ detail.code }}</dt>
                                        <dd v-html="detail.message"></dd>
                                      </div>
                                    </dl>
                                  </div>
                                </div>
                              </dd>
                            </dl>
                          </div>
                        </div>
                      </div>
                    </li>
                  </template>
                  <template v-else>
                    <!-- STEP5: 登録結果（成功） -->
                    <li class="item on item--point">
                      <div class="item__inner">
                        <div class="item__title">
                          <figure>
                            <figcaption>STEP5</figcaption>
                            <img
                              src="../../../../djangoapp/assets/teacher/img/common/icon_regi_step4.png"
                              alt="import successful"
                              class="adjust2"
                            >
                          </figure>
                        </div>
                        <div class="item__body">
                          <div class="title">
                            登録結果
                          </div>
                          <div class="result">
                            <dl class="list">
                              <dt class="list__title">ファイル名</dt>
                              <dd class="list__data">
                                <p class="text">{{ uploadedResult.filename }}</p>
                              </dd>
                              <dt class="list__title">結果</dt>
                              <dd class="list__data">
                                <p class="text text--success">
                                  <span>成功</span>（{{ uploadedResult.number_of_all_students }}名を読み込みました）
                                </p>
                              </dd>
                              <dt class="list__title">登録内容</dt>
                              <dd class="list__data">
                                <div class="c-table-list__wrapper">
                                  <div class="c-table-list">
                                    <dl class="c-table-list__inner">
                                      <div class="c-table-list__item">
                                        <dt>学級</dt>
                                        <dd>人数</dd>
                                      </div>
                                      <div
                                        class="c-table-list__item"
                                        v-for="classroom in uploadedResult.classrooms"
                                        :key="classroom.name"
                                      >
                                        <dt>{{ classroom.name }}</dt>
                                        <dd>{{ classroom.number_of_students }}</dd>
                                      </div>
                                    </dl>
                                  </div>
                                </div>
                              </dd>
                            </dl>
                          </div>
                        </div>
                      </div>
                    </li>

                    <!-- 完了 -->
                    <li class="item on item--point">
                      <div class="item__inner">
                        <div class="item__title">
                          <figure>
                            <img
                              src="../../../../djangoapp/assets/teacher/img/common/icon_regi_step_comp.png"
                              alt="complete"
                              class="adjust3"
                            >
                          </figure>
                        </div>
                        <div class="item__body">
                          <div class="title">
                            完了
                          </div>
                          <div class="body">
                            <p class="text">進級処理用の名簿データが登録されました。</p>
                          </div>
                          <div class="backLink">
                            <a
                              :href="toAcademicSessionClassroomListUri()"
                              class="c-button-blue -arrow -type01"
                            >
                              <span class="label">学級・生徒管理画面へ</span>
                            </a>
                          </div>
                        </div>
                      </div>
                    </li>

                  </template>
                </template>

              </ol>
            </div>

          </section>

          <div
            v-else
            class="c-frame-message"
          >
            <p class="c-parts-error-message -center">
              {{ targetAcademicSessions.from_academic_session.year }}年度{{ fromAcademicYear() }}年生に進級処理が可能な生徒の登録がありません。<br>
              詳しくは
              <a
                href="https://manavi.wikipy.jp/teacher/p712"
                target="_blank"
                class="c-parts-link"
              >サポートページ</a>
              を参照してください。
            </p>
          </div>
        </template>
      </template>

      <div
        v-else
        class="c-frame-message"
      >
        <p class="c-parts-error-message -center">
          {{ academicSessionYear }}年度は、現在進級処理できない年度です。現在進級処理できるのは、{{ targetAcademicSessions?.from_academic_session.year }}年度の生徒です。<br>
          詳しくは
          <a
            href="https://manavi.wikipy.jp/teacher/p712"
            target="_blank"
            class="c-parts-link"
          >サポートページ</a>
          を参照してください。
        </p>
      </div>
    </div>

    <!-- アップロード確認モーダル -->
    <confirm-modal
      :is-open="isOpenUploadConfirmModal"
      ok-label="アップロードする"
      @ok="openUploadModal"
      @cancel="closeUploadConfirmModal"
    >
      進級先の学年に生徒が存在しています。<br>
      新しい名簿データで置き換えられます。<br>
      よろしいですか？
    </confirm-modal>
    <!-- Excelアップロードモーダル -->
    <upload-modal
      :is-open="isOpenUploadModal"
      @upload="uploadStudentsRoster"
      @cancel="closeUploadModal"
    ></upload-modal>
    <!-- アップロード中モーダル -->
    <in-progress-modal
      :is-open="isOpenUploadInProgressModal"
      text="アップロード中"
    >
      ただいま登録中です。登録完了後に登録結果が表示されます。<br>
      このままお待ちください。
    </in-progress-modal>
    <error-modal></error-modal>
  </main>
</template>

<script setup lang="ts">
  import { ref, computed, onMounted } from "vue";
  import { ITargetAcademicSessions, IStudentsRegistrationStatus, IStudentsRosterImportResult, IAcademicYearClass } from "@/consts/interfaces";
  import { useNextGradeStore } from "@/store/teacher/next_grade_store";
  import { useLearningProgressStore } from "@/store/teacher/learning_progress_store";

  const nextGradeStore = useNextGradeStore()

  const learningProgressStore = useLearningProgressStore()

  const props = defineProps<{
    // 先生が選択した進級元年度
    academicSessionYear: number,
    // 先生が選択した進級先学年
    toAcademicYear: number
  }>();

  // 進級対象年度(進級元年度/進級先年度)
  const targetAcademicSessions =  ref<ITargetAcademicSessions>(null);
  // 進級元学年の生徒登録状況
  const fromAcademicYearStudentsRegistrationStatus = ref<Array<IStudentsRegistrationStatus>>(null);

  // 進級可能な年度である
  const canNextGrade = ref<boolean>(true);
  // 進級元の学年に生徒が登録されている
  const isStudentsRegisteredInFromAcademicYear = ref<boolean>(true);

  const isOpenUploadConfirmModal = ref(false)
  const isOpenUploadModal = ref(false)
  const isOpenUploadInProgressModal = ref(false)

  const academicYearsClassrooms = ref<IAcademicYearClass>(null)

  // toAcademicSessionIdとtoAcademicYearに一致する、進級先年度・進級先学年の学級を返す
  // FIXME: この処理はacademicYearsClassroomsから絞り込んでいるので、処理が重くなるなどあれば、必要なデータのみ受け取るように変更する
  const toClassrooms = computed(() => {
    // APIが実行されていない場合、値がセットされないのでnullを返す
    if (!academicYearsClassrooms.value) return null

    const toAcademicSessionId = targetAcademicSessions.value.to_academic_session.id
    const toAcademicYear = props.toAcademicYear

    // 学級がないと画面遷移できないため、classroomが存在しないケースは考慮しない
    const classrooms = academicYearsClassrooms.value.classrooms

    return classrooms[toAcademicSessionId]?.[toAcademicYear] ?? null
  })

  const uploadedResult = ref<IStudentsRosterImportResult>(null)

  onMounted(async() => {
    targetAcademicSessions.value = await nextGradeStore.fetchAcademicSessions()

    if (targetAcademicSessions.value.from_academic_session.year !== props.academicSessionYear) {
      canNextGrade.value = false
      return
    }

    fromAcademicYearStudentsRegistrationStatus.value = await nextGradeStore.fetchStudentsRegistrationStatus(
      {
        academic_session: targetAcademicSessions.value.from_academic_session.id,
        academic_year: fromAcademicYear(),
      }
    )

    // 学年を指定して学年別生徒登録状況を取得しているため、
    // fromAcademicYearStudentsRegistrationStatus の要素数は常に1 (指定した学年の分のみ)
    if (!fromAcademicYearStudentsRegistrationStatus.value[0].is_registered) {
      isStudentsRegisteredInFromAcademicYear.value = false
      return
    }

    academicYearsClassrooms.value = await learningProgressStore.fetchYearClass()
  })

  // 生徒名簿ダウンロード
  async function downloadStudentsRoster() {
    const response = await nextGradeStore.fetchDownloadStudentsRoster(
      {
        academic_session: targetAcademicSessions.value.from_academic_session.id,
        academic_year: fromAcademicYear(),
      }
    )

    if (!response) {
      console.log("Failed to download StudentsRoster.")
      return
    }
    const file = window.URL.createObjectURL(new Blob([response.data]))
    const fileName = response.headers["filename"]
    const link = document.createElement("a")
    link.href = file
    link.setAttribute("download", decodeURI(fileName))
    document.body.appendChild(link)
    link.click()
  }

  // [アップロード] ボタン押下時
  async function showUploadModal() {
    // 進級先学年に生徒が登録済みか確認する
    const studentsRegistrationStatus = await nextGradeStore.fetchStudentsRegistrationStatus(
      {
        academic_session: targetAcademicSessions.value.to_academic_session.id,
        academic_year: props.toAcademicYear,
      }
    )
    if (!studentsRegistrationStatus) {
      console.log("Failed to fetch studentsRegistrationStatus.")
      return
    }
    const isRegistered = studentsRegistrationStatus[0].is_registered
    if (isRegistered) {
      openUploadConfirmModal()
    } else {
      openUploadModal()
    }
  }

  // 生徒名簿アップロード
  async function uploadStudentsRoster(uploadFile) {
    closeUploadModal()
    openUploadInProgressModal()

    if (!uploadFile) {
      // upload ファイルなかったら送信しない
      console.log("UploadFile is not selected.")
      return
    }

    let formData = new FormData();
    formData.append("students_roster_file", uploadFile)
    formData.append("from_academic_session", targetAcademicSessions.value.from_academic_session.id.toString(10))
    formData.append("from_academic_year",  fromAcademicYear().toString(10))
    formData.append("to_academic_session", targetAcademicSessions.value.to_academic_session.id.toString(10))
    formData.append("to_academic_year",  props.toAcademicYear.toString(10))

    uploadedResult.value = await nextGradeStore.fetchNextGradeImport(
        formData
    )

    closeUploadInProgressModal()
  }

  function fromAcademicYear() {
    return props.toAcademicYear - 1
  }

  // 進級先年度の学級・生徒管理画面へのURI
  function toAcademicSessionClassroomListUri() {
    return `/teacher/class/?academic_session_year=${targetAcademicSessions.value.to_academic_session.year}`
  }

  function openUploadConfirmModal() {
    isOpenUploadConfirmModal.value = true
  }

  function closeUploadConfirmModal() {
    isOpenUploadConfirmModal.value = false
  }

  function openUploadModal() {
    closeUploadConfirmModal()
    isOpenUploadModal.value = true
  }

  function closeUploadModal() {
    isOpenUploadModal.value = false
  }

  function openUploadInProgressModal() {
    isOpenUploadInProgressModal.value = true
  }

  function closeUploadInProgressModal() {
    isOpenUploadInProgressModal.value = false
  }

  // 学級情報を再取得
  async function reloadClassrooms() {
    academicYearsClassrooms.value = await learningProgressStore.fetchYearClass()
  }

</script>
